import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Spinner } from '../../components/ui/spinner';
import { formatCurrency, formatDate } from '../../lib/i18n';
import {
  Plus,
  Search,
  User,
  Phone,
  Wrench,
  ChevronRight,
  X,
  Bike,
  ArrowUpDown,
  AlertCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SORT_OPTIONS = {
  newest: { en: 'Newest First', bn: 'নতুন প্রথমে' },
  oldest: { en: 'Oldest First', bn: 'পুরাতন প্রথমে' },
  most_spent: { en: 'Most Spent', bn: 'সবচেয়ে বেশি খরচ' },
  most_due: { en: 'Most Due', bn: 'সবচেয়ে বেশি বাকি' },
  name_asc: { en: 'Name (A-Z)', bn: 'নাম (A-Z)' },
};

export default function CustomersListPage() {
  const { garageSlug } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  // Build base path for navigation
  const basePath = garageSlug ? `/${garageSlug}/admin` : '/admin';

  useEffect(() => {
    fetchCustomers();
  }, []);

  // Debounced auto-search on typing or sort change
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(true);
      fetchCustomers();
    }, 350);
    return () => clearTimeout(timer);
  }, [search, sortBy]);

  const fetchCustomers = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (sortBy && sortBy !== 'newest') params.set('sort_by', sortBy);
      // Fetch all customers (no client-side pagination yet)
      params.set('limit', '10000');
      const qs = params.toString();
      const response = await axios.get(`${API}/customers${qs ? `?${qs}` : ''}`);
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold font-['Barlow_Condensed']">{t('customers_title')}</h1>
            <p className="text-slate-500">{language === 'en' ? `${customers.length} customers` : `${customers.length}টি গ্রাহক`}</p>
          </div>
          <Link to={`${basePath}/customers/new`} data-testid="new-customer-btn">
            <Button className="gap-2 bg-blue-800 hover:bg-blue-900">
              <Plus className="w-4 h-4" />
              {t('new_customer')}
            </Button>
          </Link>
        </div>

        {/* Search + Sort */}
        <Card>
          <CardContent className="p-4">
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  placeholder={language === 'en' ? 'Search by name, phone, email, address, vehicle...' : 'নাম, ফোন, ইমেইল, ঠিকানা, বাইক দিয়ে খুঁজুন...'}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 pr-9 h-12"
                  data-testid="customer-search"
                />
                {search && (
                  <button
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-red-400 hover:text-red-600 transition-colors"
                    onClick={() => setSearch('')}
                    aria-label="Clear search"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                className="h-12 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
              >
                {Object.entries(SORT_OPTIONS).map(([key, labels]) => (
                  <option key={key} value={key}>{labels[language] || labels.en}</option>
                ))}
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Customers List */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Spinner size="lg" />
          </div>
        ) : customers.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {customers.map((customer) => (
              <Link 
                key={customer.id} 
                to={`${basePath}/customers/${customer.id}`}
                data-testid={`customer-card-${customer.id}`}
              >
                <Card className="hover:border-blue-500 transition-colors cursor-pointer h-full">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                          <User className="w-6 h-6 text-blue-800" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">{customer.name}</h3>
                          <div className="flex items-center gap-1 text-sm text-slate-500">
                            <Phone className="w-3 h-3" />
                            {customer.phone}
                          </div>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </div>
                    
                    <div className={`mt-4 pt-4 border-t border-slate-100 grid gap-4 ${customer.total_due > 0 ? 'grid-cols-3' : 'grid-cols-2'}`}>
                      <div>
                        <p className="text-xs text-slate-500">{t('total_jobs')}</p>
                        <div className="flex items-center gap-1">
                          <Wrench className="w-4 h-4 text-blue-600" />
                          <span className="font-bold">{customer.total_jobs || 0}</span>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">{t('total_spent')}</p>
                        <p className="font-bold text-green-600">{formatCurrency(customer.total_spent, language)}</p>
                      </div>
                      {customer.total_due > 0 && (
                        <div>
                          <p className="text-xs text-slate-500">{t('total_due')}</p>
                          <p className="font-bold text-red-600">{formatCurrency(customer.total_due, language)}</p>
                        </div>
                      )}
                    </div>
                    {customer.bike_names && customer.bike_names.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-slate-100">
                        <div className="flex items-center gap-1 flex-wrap">
                          <Bike className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <span className="text-xs text-slate-500 truncate">{customer.bike_names.join(', ')}</span>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-20 text-center">
              <User className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500 text-lg mb-4">
                {language === 'en' ? 'No customers found' : 'কোন গ্রাহক পাওয়া যায়নি'}
              </p>
              <Link to="/admin/customers/new">
                <Button className="bg-blue-800 hover:bg-blue-900">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('new_customer')}
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
}
